import type { RunnerReport } from "@trader/shared/Money"
import { formatDecimal } from "@trader/shared/Money"
import { useMemo } from "react"
import { compareFills, type FillMatch, MATCH_LABELS } from "./compareFills.ts"
import { formatMoney, formatTime } from "./format.ts"

const tone: Readonly<Record<FillMatch, string>> = {
  match: "up",
  later: "muted",
  price: "warn",
  qty: "warn",
  missing: "down",
  extra: "down"
}

const number = (value: number, precision: number) => value.toLocaleString("en-IN", { maximumFractionDigits: precision })

/**
 * Backtest vs paper (docs/design/0007): the strategy's backtest trades (run
 * forward on the account's bars) next to the account's paper fills, trade by trade.
 */
export function BacktestVsPaper({ currency, pricePrecision, report }: {
  readonly report: RunnerReport
  readonly currency: string
  readonly pricePrecision: number
}) {
  const comparison = useMemo(() => compareFills(report.engineFills, report.paperFills), [report])
  if (comparison.total === 0) {
    return <p className="muted">No trades yet: the backtest and paper fills show here side by side as the replay plays.</p>
  }
  return (
    <div className="backtest-vs-paper">
      <p className={comparison.matched === comparison.total ? "up" : "muted"}>
        {comparison.matched} of {comparison.total} fills the same in the backtest and in paper · paper charges{" "}
        {formatMoney(comparison.charges, currency)}
      </p>
      <table className="tester-table">
        <thead>
          <tr>
            <th className="text">Bar</th>
            <th className="text">Side</th>
            <th>Backtest</th>
            <th>Paper</th>
            <th>Price diff</th>
            <th>Charges</th>
            <th className="text">Result</th>
          </tr>
        </thead>
        <tbody>
          {comparison.pairs.map((pair, index) => (
            <tr key={index}>
              <td className="text">{formatTime(pair.backtest?.time ?? pair.paper?.time)}</td>
              <td className={pair.side === "Buy" ? "text up" : "text down"}>{pair.side}</td>
              <td>{pair.backtest ? `${number(pair.backtest.qty, 4)} @ ${number(pair.backtest.price, pricePrecision)}` : "—"}</td>
              <td>{pair.paper ? `${formatDecimal(pair.paper.qty)} @ ${formatDecimal(pair.paper.price)}` : "—"}</td>
              <td>{pair.priceDiff ? formatDecimal(pair.priceDiff) : "—"}</td>
              <td>{pair.paper ? formatMoney(pair.paper.fee.total, currency) : "—"}</td>
              <td className={`text ${tone[pair.match]}`}>{MATCH_LABELS[pair.match]}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
